import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

// Use empty string for same-origin API calls (works on both preview and production)
const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// Get session data from sessionStorage
const getSessionData = () => {
  try {
    const data = sessionStorage.getItem('dailyCodingSession');
    if (data) {
      return JSON.parse(data);
    }
  } catch (e) {
    console.error('Error reading session data:', e);
  }
  return null;
};

export default function AuthCallback({ setUser }) {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL hash
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.error("No session_id found in URL");
          navigate("/");
          return;
        }

        const sessionId = sessionIdMatch[1];

        // Get anonymous session data before auth (for merging)
        const anonymousSessionData = getSessionData();
        const wasInChallengeMode = anonymousSessionData?.dailyQuestionsCompleted >= 3;
        const hadWarmUpProgress = anonymousSessionData?.dailyQuestionsCompleted > 0 && anonymousSessionData?.dailyQuestionsCompleted < 3;
        const hadSessionData = anonymousSessionData && (
          (anonymousSessionData.answeredQuestions?.length > 0) || 
          (anonymousSessionData.sessionScore > 0)
        );

        // Exchange session_id for session_token
        const response = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (!response.ok) {
          throw new Error("Failed to authenticate");
        }

        const userData = await response.json();
        
        // Merge anonymous session data with user account (always if there's session data)
        if (hadSessionData) {
          try {
            const mergeResponse = await fetch(`${API}/user/merge-session`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                answered_questions: anonymousSessionData.answeredQuestions || [],
                session_score: anonymousSessionData.sessionScore || 0,
                daily_questions_completed: anonymousSessionData.dailyQuestionsCompleted || 0,
                challenges_completed: anonymousSessionData.challengesCompleted || 0
              })
            });
            
            if (mergeResponse.ok) {
              const mergeResult = await mergeResponse.json();
              console.log("Session merged:", mergeResult);
              
              // Update user's total score with merged points
              if (mergeResult.points_added > 0) {
                userData.total_score = (userData.total_score || 0) + mergeResult.points_added;
              }
            }
          } catch (mergeError) {
            console.error("Error merging session:", mergeError);
          }
          
          // Clear anonymous session after merge
          sessionStorage.removeItem('dailyCodingSession');
        }
        
        // Set user in parent state
        if (setUser) {
          setUser(userData);
        }
        
        // Clear hash from URL and navigate
        window.history.replaceState(null, "", window.location.pathname);
        
        // Navigate based on context - show success message if points were merged
        const navigationState = { user: userData };
        
        if (hadSessionData) {
          navigationState.pointsMerged = true;
        }
        
        if (wasInChallengeMode) {
          navigationState.startInChallengeMode = true;
        } else if (hadWarmUpProgress) {
          // User was making warm-up progress, continue from there
          navigationState.warmUpProgress = anonymousSessionData.dailyQuestionsCompleted;
        }
        
        if (userData.is_admin) {
          navigate("/admin", { state: navigationState });
        } else {
          navigate("/", { state: navigationState });
        }
      } catch (error) {
        console.error("Auth error:", error);
        navigate("/");
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
}
